import { request } from "../../utils/request";
import { getApiOrigin } from "../../config/apiBase";
import { triggerBlobDownload } from "../../utils/batchVideoDownload";

type ApiResult<T> = AiTranscription.CommonResult<T>;

function appendResultTypes(form: FormData, resultTypes: AiTranscription.ResultType[]) {
  resultTypes.forEach((type) => form.append("resultTypes", type));
}

export function createAiTranscriptionRequestId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `web-${Date.now()}-${Math.random().toString(16).slice(2)}`.slice(0, 64);
}

export function buildAiTranscriptionFormData(params: {
  file: File;
  resultTypes: AiTranscription.ResultType[];
  rewriteRequirement?: string;
  requestId?: string;
}): FormData {
  const form = new FormData();
  form.append("file", params.file, params.file.name);
  appendResultTypes(form, params.resultTypes);
  const rewrite = params.rewriteRequirement?.trim();
  if (rewrite) form.append("rewriteRequirement", rewrite);
  if (params.requestId) form.append("requestId", params.requestId);
  return form;
}

export function buildAiTranscriptionPointPreviewFormData(params: {
  resultTypes: AiTranscription.ResultType[];
  rewriteRequirement?: string;
  assetType?: AiTranscription.AssetType;
  durationSeconds?: number;
  file?: File | null;
}): FormData {
  const form = new FormData();
  appendResultTypes(form, params.resultTypes);
  const rewrite = params.rewriteRequirement?.trim();
  if (rewrite) form.append("rewriteRequirement", rewrite);
  if (params.assetType === "VIDEO" || params.assetType === "AUDIO") {
    form.append("assetType", params.assetType);
    form.append("durationSeconds", String(Math.max(1, Math.ceil(Number(params.durationSeconds) || 0))));
  } else if (params.file) {
    form.append("file", params.file, params.file.name);
  }
  return form;
}

export const previewAiTranscriptionPointApi = (form: FormData) =>
  request<ApiResult<AiTranscription.Quote>>("/ai-transcription/point-preview", {
    method: "POST",
    body: form,
  });

export const createAiTranscriptionTaskApi = (form: FormData) =>
  request<ApiResult<AiTranscription.Task>>("/ai-transcription/tasks", {
    method: "POST",
    body: form,
  });

export const getAiTranscriptionTasksApi = (params?: { status?: string; keyword?: string }) =>
  request<ApiResult<AiTranscription.Task[]>>("/ai-transcription/tasks", {
    method: "GET",
    params,
  });

export const getAiTranscriptionTaskApi = (id: number) =>
  request<ApiResult<AiTranscription.Task>>(`/ai-transcription/tasks/${id}`, {
    method: "GET",
  });

export const quoteAiTranscriptionTaskApi = (id: number, resultTypes: AiTranscription.ResultType[]) =>
  request<ApiResult<AiTranscription.Quote>>(`/ai-transcription/tasks/${id}/quotes`, {
    method: "POST",
    body: { resultTypes },
  });

export const operateAiTranscriptionTaskApi = (id: number, body: AiTranscription.OperationBody) =>
  request<ApiResult<AiTranscription.Task>>(`/ai-transcription/tasks/${id}/operations`, {
    method: "POST",
    body,
  });

export const deleteAiTranscriptionTaskApi = (id: number) =>
  request<ApiResult<boolean>>(`/ai-transcription/tasks/${id}`, {
    method: "DELETE",
  });

export const getAiTranscriptionTemplatesApi = () =>
  request<ApiResult<AiTranscription.Template[]>>("/ai-transcription/templates", {
    method: "GET",
  });

export const getAiTranscriptionTemplateApi = (id: number) =>
  request<ApiResult<AiTranscription.Template>>(`/ai-transcription/templates/${id}`, {
    method: "GET",
  });

export async function exportAiTranscriptionTaskApi(params: {
  id: number;
  scope: AiTranscription.ExportScope;
  resultType?: AiTranscription.ResultType;
  filename: string;
}): Promise<void> {
  const token = (localStorage.getItem("token") || "").trim();
  const query = new URLSearchParams({ scope: params.scope });
  if (params.scope === "CURRENT" && params.resultType) {
    query.set("resultType", params.resultType);
  }
  const url = `${getApiOrigin()}/ai-transcription/tasks/${params.id}/export?${query.toString()}`;
  const res = await fetch(url, {
    method: "GET",
    headers: token
      ? { Authorization: token.startsWith("Bearer ") ? token : `Bearer ${token}` }
      : {},
  });
  const contentType = (res.headers.get("content-type") || "").toLowerCase();
  if (contentType.includes("application/json")) {
    const data = await res.json().catch(() => null);
    const message = data?.msg || `导出失败 (${res.status})`;
    throw Object.assign(new Error(message), { msg: message, response: { data, msg: data?.msg } });
  }
  if (!res.ok) {
    throw new Error(`导出失败 (${res.status})`);
  }
  const blob = await res.blob();
  if (!blob.size) throw new Error("导出文件为空");
  triggerBlobDownload(blob, params.filename);
}
